import { BaseModel, type RelationConfig } from './base';
import { dateYMDRelation, type DateYMD } from '../lib/dates';

// Enums

export enum EmployeeHourRosterStatus {
  working = 'working',
  notWorking = 'notWorking',
  leave = 'leave',
}

export const EmployeeHourRosterStatusLabels: Record<EmployeeHourRosterStatus, string> = {
  [EmployeeHourRosterStatus.working]: 'roster.working',
  [EmployeeHourRosterStatus.notWorking]: 'roster.notWorking',
  [EmployeeHourRosterStatus.leave]: 'roster.leave',
};

export enum EmployeeHourRosterFrequency {
  weekly = 1,
  fortnightly = 2,
  monthly = 4,
}

export const EmployeeHourRosterFrequencyLabels: Record<EmployeeHourRosterFrequency, string> = {
  [EmployeeHourRosterFrequency.weekly]: 'datetime.range.weekly',
  [EmployeeHourRosterFrequency.fortnightly]: 'datetime.range.fortnightly',
  [EmployeeHourRosterFrequency.monthly]: 'datetime.range.monthly',
};

export enum EmployeeHourRosterWeek {
  a = 1,
  b = 2,
  c = 3,
  d = 4,
}

export const EmployeeHourRosterWeekLabels: Record<EmployeeHourRosterWeek, string> = {
  [EmployeeHourRosterWeek.a]: 'api.models.employeeHour.a',
  [EmployeeHourRosterWeek.b]: 'api.models.employeeHour.b',
  [EmployeeHourRosterWeek.c]: 'api.models.employeeHour.c',
  [EmployeeHourRosterWeek.d]: 'api.models.employeeHour.d',
};

export enum EmployeeHourType {
  none = 'none',
  default = 'default',
  override = 'override',
}

export const EmployeeHourTypeLabels: Record<EmployeeHourType, string> = {
  [EmployeeHourType.none]: 'general.none',
  [EmployeeHourType.default]: 'general.default',
  [EmployeeHourType.override]: 'general.override',
};

export enum EmployeeHourLeaveType {
  none = 0,
  sick = 1,
  holiday = 2,
  other = 3,
}

export const EmployeeHourLeaveTypeLabels: Record<EmployeeHourLeaveType, string> = {
  [EmployeeHourLeaveType.none]: 'general.none',
  [EmployeeHourLeaveType.sick]: 'roster.sick',
  [EmployeeHourLeaveType.holiday]: 'roster.holiday',
  [EmployeeHourLeaveType.other]: 'general.other',
};

// Model

export class EmployeeHour extends BaseModel {
  // Ported from the Nuxt 2 `date: { type: DateYMD }` relation. Consumers
  // across the roster/schedule views read DateYMD shape on this field.
  date!: DateYMD;
  type!: EmployeeHourType;
  rosterFrequency!: EmployeeHourRosterFrequency;
  rosterWeek!: EmployeeHourRosterWeek;
  leaveType!: EmployeeHourLeaveType;
  working!: boolean;
  startTime!: number;
  finishTime!: number;
  label!: string;

  static override relations(): RelationConfig {
    return {
      date: dateYMDRelation,
    };
  }

  get isDefaultHours(): boolean {
    return this.type === EmployeeHourType.default;
  }

  get isOverrideHours(): boolean {
    return this.type === EmployeeHourType.override;
  }

  get isLeaveTypeNone(): boolean {
    return this.leaveType === EmployeeHourLeaveType.none;
  }

  get isLeaveTypeSick(): boolean {
    return this.leaveType === EmployeeHourLeaveType.sick;
  }

  get isLeaveTypeHoliday(): boolean {
    return this.leaveType === EmployeeHourLeaveType.holiday;
  }

  get isLeaveTypeOther(): boolean {
    return this.leaveType === EmployeeHourLeaveType.other;
  }

  get isOnLeave(): boolean {
    return !this.isLeaveTypeNone;
  }

  get isRosterFrequencyWeekly(): boolean {
    return this.rosterFrequency === EmployeeHourRosterFrequency.weekly;
  }

  get isRosterFrequencyFortnightly(): boolean {
    return this.rosterFrequency === EmployeeHourRosterFrequency.fortnightly;
  }

  get isRosterFrequencyMonthly(): boolean {
    return this.rosterFrequency === EmployeeHourRosterFrequency.monthly;
  }

  get status(): EmployeeHourRosterStatus {
    if (this.isOnLeave) {
      return EmployeeHourRosterStatus.leave;
    }

    if (this.working) {
      return EmployeeHourRosterStatus.working;
    }

    return EmployeeHourRosterStatus.notWorking;
  }

  set status(value: EmployeeHourRosterStatus) {
    if (value === EmployeeHourRosterStatus.leave) {
      this.working = false;
      this.leaveType = EmployeeHourLeaveType.holiday;
    } else if (value === EmployeeHourRosterStatus.working) {
      this.working = true;
      this.leaveType = EmployeeHourLeaveType.none;
    } else {
      this.working = false;
      this.leaveType = EmployeeHourLeaveType.none;
    }
  }
}
